/**
 * FloorCanvas — the single Skia canvas hosting the whole floor.
 *
 * Composition (bottom → top): grid picture → static-plane picture →
 * `children` (the DYNAMIC plane: selection, handles, guides, marquee — M5),
 * all inside ONE `<Group>` whose transform derives from the camera shared
 * values. Panning/zooming updates that derived transform on the UI thread —
 * the recorded pictures re-blit with zero React involvement.
 *
 * @module floorplan/renderer/FloorCanvas
 * @category FloorPlan
 */

import { useEffect } from 'react';
import { Platform } from 'react-native';
import { useDerivedValue } from 'react-native-reanimated';
import type { SkPicture } from '@shopify/react-native-skia';
import {
  Canvas,
  Group,
  Picture,
  useCanvasRef,
} from '@shopify/react-native-skia';
import type { ReactNode } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';

import type { FloorCamera } from '../viewport/useFloorCamera';

type Props = {
  camera: FloorCamera;
  /** Grid + surface picture (below everything). */
  gridPicture: SkPicture | null;
  /** The recorded static plane (all non-selected objects). */
  staticPicture: SkPicture | null;
  /** Dynamic-plane Skia elements rendered INSIDE the world transform. */
  children?: ReactNode;
  /** Screen-space overlay elements rendered OUTSIDE the world transform. */
  overlay?: ReactNode;
  style?: StyleProp<ViewStyle>;
};

const FloorCanvas = ({
  camera,
  gridPicture,
  staticPicture,
  children,
  overlay,
  style,
}: Props) => {
  const canvasRef = useCanvasRef();

  // translate-then-scale ⇒ screen = world * zoom + t (matches utils/camera).
  const transform = useDerivedValue(() => [
    { translateX: camera.tx.get() },
    { translateY: camera.ty.get() },
    { scale: camera.zoom.get() },
  ]);

  // Force a present when a recorded picture changes (grid/snap toggle, object
  // edit) WITHOUT a camera move. On web the canvas' only live invalidation
  // source is the Reanimated camera transform, so a static prop-only change
  // (e.g. toggling the grid) doesn't re-present until the next pan/zoom —
  // the grid appears frozen. An explicit redraw closes that gap; it's a
  // harmless no-op on native, which already repaints on the React re-render.
  useEffect(() => {
    const safeRedraw = () => {
      const canvas = canvasRef.current;
      // `redraw()` dereferences the BARE global `SkiaViewApi` inside
      // react-native-skia — a ReferenceError (not undefined) when the web
      // runtime global is missing (Sentry FUZE-STORE-APP-4MT). Skipping the
      // nudge is benign: the next camera frame re-presents anyway, and if the
      // global is genuinely absent the canvas isn't rendering at all.
      const skiaViewApi = (globalThis as { SkiaViewApi?: unknown }).SkiaViewApi;
      if (canvas === null || skiaViewApi === undefined) return;
      canvas.redraw();
    };
    safeRedraw();
    if (Platform.OS !== 'web') return undefined;
    // WEB retry ladder: on a cold load the lazily-mounted CanvasKit surface
    // can bind AFTER the first pictures were recorded, silently dropping the
    // single redraw above and leaving the floor blank until the next
    // pan/zoom (same late-binding class as the editor's camera-fit ladder).
    // A few spaced re-presents are cheap no-ops once the frame is up.
    const timers = [80, 400, 1500].map((delay) =>
      setTimeout(safeRedraw, delay),
    );
    return () => {
      for (const timer of timers) clearTimeout(timer);
    };
  }, [canvasRef, gridPicture, staticPicture]);

  return (
    <Canvas ref={canvasRef} style={style}>
      <Group transform={transform}>
        {gridPicture !== null ? <Picture picture={gridPicture} /> : null}
        {staticPicture !== null ? <Picture picture={staticPicture} /> : null}
        {children}
      </Group>
      {overlay}
    </Canvas>
  );
};

export default FloorCanvas;
